import { Navigate, Outlet } from 'react-router-dom';

import { useGetCurrentUserQuery } from '@/store/api/userApi';
import { useAppSelector } from '@/store/hooks';
import {
  selectIsAdvisorAdminOfSelectedCompany,
  selectIsCompanyAdmin,
  selectIsPlatformAdmin,
  selectIsSuperUser,
} from '@/store/slices/userSlice';

import { LoadingIndicator } from '@/components/common/Loading';

import { CompanyRole, RoutePath, Side } from '@/utils/enums';

// Company admins
export const AdminGuard = () => {
  const isUserSuperUser = useAppSelector(selectIsSuperUser);
  const isUserCompanyAdmin = useAppSelector(selectIsCompanyAdmin);

  const { data: user } = useGetCurrentUserQuery();
  const userId = user?.userId;

  if (!userId) return <LoadingIndicator />;
  if (!isUserSuperUser && !isUserCompanyAdmin) {
    return <Navigate to="/" replace />;
  }
  return <Outlet />;
};

// Super users (platform admin, tech support)
export const SuperUserGuard = () => {
  const isUserSuperUser = useAppSelector(selectIsSuperUser);
  const { data: user } = useGetCurrentUserQuery();
  const userId = user?.userId;

  if (!userId) return <LoadingIndicator />;
  if (!isUserSuperUser) {
    return <Navigate to="/" replace />;
  }
  return <Outlet />;
};

// Users who are company admins of the selected advisor company
// Only admin is available
export const AdvisorAdminGuard = () => {
  const { data: user } = useGetCurrentUserQuery();
  const userId = user?.userId;
  const isUserAdvisorAdminOfSelectedCompany = useAppSelector(
    selectIsAdvisorAdminOfSelectedCompany
  );

  if (!userId) return <LoadingIndicator />;
  if (isUserAdvisorAdminOfSelectedCompany) {
    return <Navigate to={RoutePath.ADMIN} replace />;
  }
  return <Outlet />;
};

// Users who are regular users for the selected advisor company
// Only settings is available
export const AdvisorGuard = () => {
  const { data: user } = useGetCurrentUserQuery();
  const userId = user?.userId;
  const selectedCompany = useAppSelector(state => state.user.selectedCompany);
  const isUserSuperUser = useAppSelector(selectIsSuperUser);
  const isUserAdvisorAdminOfSelectedCompany = useAppSelector(
    selectIsAdvisorAdminOfSelectedCompany
  );

  if (!userId) return <LoadingIndicator />;
  if (
    selectedCompany?.companyRole === CompanyRole.ADVISOR &&
    !isUserAdvisorAdminOfSelectedCompany &&
    !isUserSuperUser
  ) {
    return <Navigate to={RoutePath.SETTINGS_ACCOUNT} replace />;
  }
  return <Outlet />;
};

// Seller side only
export const SellerGuard = () => {
  const selectedSide = useAppSelector(state => state.user.selectedSide);

  if (!selectedSide) return <LoadingIndicator />;
  if (selectedSide !== Side.SELLER) return <Navigate to="/" replace />;
  return <Outlet />;
};

// Platform admins only (excludes tech support)
export const PlatformAdminGuard = () => {
  const isPlatformAdmin = useAppSelector(selectIsPlatformAdmin);
  const { data: user } = useGetCurrentUserQuery();
  const userId = user?.userId;

  if (!userId) return <LoadingIndicator />;
  if (!isPlatformAdmin) {
    return <Navigate to={RoutePath.DASHBOARD} replace />;
  }
  return <Outlet />;
};
